/**
 * v-tooltip directive
 * Replaces native title/tooltip with a custom styled tooltip matching InfoTooltip design.
 *
 * Usage:
 *   <button v-tooltip="'Some helpful text'">Hover me</button>
 *   <button v-tooltip.bottom="'Bottom tooltip'">Hover me</button>
 *
 * Modifiers: top (default), bottom, left, right
 */
import type { Directive, DirectiveBinding } from 'vue'

interface TooltipState {
  el: HTMLElement
  tooltipEl: HTMLElement | null
  arrowEl: HTMLElement | null
  text: string
  position: 'top' | 'bottom' | 'left' | 'right'
  showTimer: number | undefined
  hideTimer: number | undefined
  onMouseEnter: () => void
  onMouseLeave: () => void
  onScroll: () => void
}

const stateMap = new WeakMap<HTMLElement, TooltipState>()

function getPosition(binding: DirectiveBinding): 'top' | 'bottom' | 'left' | 'right' {
  if (binding.modifiers.bottom) return 'bottom'
  if (binding.modifiers.left) return 'left'
  if (binding.modifiers.right) return 'right'
  return 'top'
}

function createTooltipEl(): { tooltip: HTMLElement; arrow: HTMLElement } {
  const tooltip = document.createElement('div')
  tooltip.className = 'v-tooltip-popup'

  const arrow = document.createElement('div')
  arrow.className = 'v-tooltip-arrow'
  tooltip.appendChild(arrow)

  const content = document.createElement('div')
  content.className = 'v-tooltip-content'
  tooltip.appendChild(content)

  return { tooltip, arrow }
}

function positionTooltip(state: TooltipState) {
  const { el, tooltipEl, arrowEl, position } = state
  if (!tooltipEl || !arrowEl) return

  const rect = el.getBoundingClientRect()
  const tooltipRect = tooltipEl.getBoundingClientRect()
  const gap = 8
  const padding = 8

  let top = 0
  let left = 0

  switch (position) {
    case 'bottom':
      top = rect.bottom + gap
      left = rect.left + rect.width / 2 - tooltipRect.width / 2
      tooltipEl.setAttribute('data-position', 'bottom')
      break
    case 'left':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2
      left = rect.left - tooltipRect.width - gap
      tooltipEl.setAttribute('data-position', 'left')
      break
    case 'right':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2
      left = rect.right + gap
      tooltipEl.setAttribute('data-position', 'right')
      break
    case 'top':
    default: {
      top = rect.top - tooltipRect.height - gap
      left = rect.left + rect.width / 2 - tooltipRect.width / 2
      // Auto fall back to bottom if not enough space on top
      if (top < padding) {
        top = rect.bottom + gap
        tooltipEl.setAttribute('data-position', 'bottom')
      } else {
        tooltipEl.setAttribute('data-position', 'top')
      }
      break
    }
  }

  // Prevent horizontal overflow
  if (left < padding) left = padding
  if (left + tooltipRect.width > window.innerWidth - padding) {
    left = window.innerWidth - padding - tooltipRect.width
  }

  tooltipEl.style.top = `${top}px`
  tooltipEl.style.left = `${left}px`
}

function showTooltip(state: TooltipState) {
  clearTimeout(state.hideTimer)
  if (!state.text) return

  state.showTimer = window.setTimeout(() => {
    if (!state.tooltipEl) {
      const { tooltip, arrow } = createTooltipEl()
      state.tooltipEl = tooltip
      state.arrowEl = arrow
      document.body.appendChild(tooltip)
    }

    const contentEl = state.tooltipEl.querySelector('.v-tooltip-content') as HTMLElement
    if (contentEl) contentEl.textContent = state.text

    state.tooltipEl.classList.add('visible')
    positionTooltip(state)
  }, 200)
}

function hideTooltip(state: TooltipState) {
  clearTimeout(state.showTimer)
  state.hideTimer = window.setTimeout(() => {
    if (state.tooltipEl) {
      state.tooltipEl.classList.remove('visible')
    }
  }, 100)
}

function destroyTooltip(state: TooltipState) {
  clearTimeout(state.showTimer)
  clearTimeout(state.hideTimer)
  if (state.tooltipEl) {
    state.tooltipEl.remove()
    state.tooltipEl = null
    state.arrowEl = null
  }
  state.el.removeEventListener('mouseenter', state.onMouseEnter)
  state.el.removeEventListener('mouseleave', state.onMouseLeave)
  document.removeEventListener('scroll', state.onScroll, true)
}

export const vTooltip: Directive<HTMLElement, string> = {
  mounted(el, binding) {
    const text = binding.value || ''
    const position = getPosition(binding)

    // Remove native title if present
    el.removeAttribute('title')

    const state: TooltipState = {
      el,
      tooltipEl: null,
      arrowEl: null,
      text,
      position,
      showTimer: undefined,
      hideTimer: undefined,
      onMouseEnter: () => showTooltip(state),
      onMouseLeave: () => hideTooltip(state),
      onScroll: () => {
        if (state.tooltipEl?.classList.contains('visible')) {
          hideTooltip(state)
        }
      },
    }

    stateMap.set(el, state)

    el.addEventListener('mouseenter', state.onMouseEnter)
    el.addEventListener('mouseleave', state.onMouseLeave)
    document.addEventListener('scroll', state.onScroll, true)
  },

  updated(el, binding) {
    const state = stateMap.get(el)
    if (!state) return

    state.text = binding.value || ''
    state.position = getPosition(binding)

    // Update content if tooltip is visible
    if (state.tooltipEl) {
      const contentEl = state.tooltipEl.querySelector('.v-tooltip-content') as HTMLElement
      if (contentEl) contentEl.textContent = state.text
    }
  },

  unmounted(el) {
    const state = stateMap.get(el)
    if (state) {
      destroyTooltip(state)
      stateMap.delete(el)
    }
  },
}
